import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { vendorApi } from '../../services/api';
import { ArrowLeft, User, TrendingUp } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import TopBar from '../../components/TopBar';

export default function CustomerDetails() {
  const { customerNo: id } = useParams<{ customerNo: string }>();
  const navigate = useNavigate();

  const { data: vendorData, error } = useQuery({
    queryKey: ['vendor', 'me'],
    queryFn: async () => {
      const response = await vendorApi.me();
      return response.data;
    },
  });

  const { data: customerData, isLoading, error: customerError } = useQuery({
    queryKey: ['vendor', 'customer', id],
    queryFn: async () => {
      const response = await vendorApi.getCustomerDetails(id!);
      return response.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (error) {
      navigate('/login');
    }
  }, [error, navigate]);

  const handleLogout = async () => {
    try {
      await vendorApi.logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      navigate('/login');
    }
  };

  // Calculate monthly chart data
  const getMonthlyStats = () => {
    const customer = customerData?.customer;
    if (!customer?.monthlyAmounts || !Array.isArray(customer.monthlyAmounts)) {
      return { months: [], current: [], lastYear: [], trend: [] };
    }

    const now = new Date();
    const months: string[] = [];
    const current: number[] = [];
    const lastYear: number[] = [];

    // Generate last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = date.toLocaleDateString('sv-SE', { month: 'short', year: 'numeric' });
      months.push(monthKey);

      // Current year - format is "YYYY-MM"
      const currentMonthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const currentAmount = customer.monthlyAmounts?.find((m: any) => m.month === currentMonthStr)?.amount || 0;
      current.push(currentAmount);

      // Last year
      const lastYearDate = new Date(date.getFullYear() - 1, date.getMonth(), 1);
      const lastYearMonthStr = `${lastYearDate.getFullYear()}-${String(lastYearDate.getMonth() + 1).padStart(2, '0')}`;
      const lastYearAmount = customer.monthlyAmounts?.find((m: any) => m.month === lastYearMonthStr)?.amount || 0;
      lastYear.push(lastYearAmount);
    }

    // Calculate trend line
    const trend = calculateTrendLine(current);

    return { months, current, lastYear, trend };
  };

  // Linear regression for trend line
  const calculateTrendLine = (data: number[]) => {
    const n = data.length;
    const xValues = Array.from({ length: n }, (_, i) => i);
    const yValues = data;

    const sumX = xValues.reduce((a, b) => a + b, 0);
    const sumY = yValues.reduce((a, b) => a + b, 0);
    const sumXY = xValues.reduce((sum, x, i) => sum + x * yValues[i], 0);
    const sumX2 = xValues.reduce((sum, x) => sum + x * x, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    return xValues.map(x => slope * x + intercept);
  };

  const { months, current: monthlyData, lastYear: lastYearData, trend: trendLine } = getMonthlyStats();
  const maxAmount = Math.max(...monthlyData, ...lastYearData, ...trendLine, 1);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Laddar kunddetaljer...</p>
        </div>
      </div>
    );
  }

  const customer = customerData?.customer;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <Sidebar
        logoUrl={vendorData?.tenant?.logoUrl}
        companyName={vendorData?.tenant?.companyName}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <TopBar
          userName={vendorData?.vendor?.fullName || vendorData?.vendor?.username}
          onLogout={handleLogout}
        />

        {/* Page Content */}
        <main className="mx-auto w-full max-w-[1400px] flex-1 px-6 py-8 lg:px-10 lg:py-10">
          {/* Back Button */}
          <button
            onClick={() => navigate('/customers')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Tillbaka till Kunder
          </button>

          {customerError || !customer ? (
            <div className="panel">
              <p role="alert" className="bg-surface p-6 text-sm text-gray-700">
                {(customerError as any)?.response?.status === 404
                  ? 'Kunden hittades inte.'
                  : 'Kunduppgifterna kunde inte hämtas. Försök igen om en stund.'}
              </p>
            </div>
          ) : (
          <>

          {/* Customer Header */}
          <div
            className="panel mb-6"
          >
            <div className="bg-surface p-6">
              <div className="flex items-center gap-4">
              <div className="bg-blue-100 rounded-full p-3">
                <User className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h1 className="font-display text-[1.35rem] leading-snug text-ink text-balance md:text-[1.6rem]">{customer?.customerName || customer?.name}</h1>
                <p className="text-gray-600">Kundnr: {customer?.customerNo}</p>
              </div>
            </div>

            {/* Customer Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              <div>
                <h3 className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-gray-500">Adress</h3>
                <div className="space-y-1">
                  <p className="text-sm">{customer?.address || '-'}</p>
                  {customer?.address2 && <p className="text-sm">{customer.address2}</p>}
                  <p className="text-sm">
                    {customer?.postCode} {customer?.city}
                  </p>
                  {customer?.countryRegionCode && <p className="text-sm">{customer.countryRegionCode}</p>}
                </div>
              </div>

              <div>
                <h3 className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-gray-500">Kontaktinformation</h3>
                <div className="space-y-1">
                  {customer?.phoneNo && (
                    <p className="text-sm">
                      <span className="font-medium">Telefon:</span> {customer.phoneNo}
                    </p>
                  )}
                  {customer?.email && (
                    <p className="text-sm">
                      <span className="font-medium">E-post:</span> {customer.email}
                    </p>
                  )}
                  {customer?.contact && (
                    <p className="text-sm">
                      <span className="font-medium">Kontaktperson:</span> {customer.contact}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-gray-500">Statistik</h3>
                <div className="space-y-1">
                  <p className="text-sm">
                    <span className="font-medium">Senaste 12 mån:</span>{' '}
                    {(customer?.amountLast12Months || 0).toLocaleString('sv-SE', {
                      style: 'currency',
                      currency: 'SEK',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">24-12 mån sedan:</span>{' '}
                    {(customer?.amount24to12MonthsAgo || 0).toLocaleString('sv-SE', {
                      style: 'currency',
                      currency: 'SEK',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </p>
                </div>
              </div>
            </div>
            </div>
          </div>

          {/* Monthly Chart */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-[15px] font-semibold text-ink">Belopp per månad (senaste 12 månaderna)</h2>
                <div className="flex items-center gap-4 mt-2 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gradient-to-t from-blue-600 to-blue-400 rounded"></div>
                    <span className="text-gray-600">Innevarande år</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-300 rounded"></div>
                    <span className="text-gray-600">Föregående år</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-0.5 bg-green-500"></div>
                    <span className="text-gray-600">Trendlinje</span>
                  </div>
                </div>
              </div>
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div className="relative" style={{ height: '320px' }}>
              {/* Y-axis labels */}
              <div className="absolute left-0 top-0 bottom-12 w-20 flex flex-col justify-between text-xs text-gray-500">
                <div className="text-right pr-2">
                  {maxAmount.toLocaleString('sv-SE', {
                    style: 'currency',
                    currency: 'SEK',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
                <div className="text-right pr-2">0 kr</div>
              </div>

              {/* Chart area */}
              <div className="absolute left-20 right-0 top-0 bottom-0">
                {/* Trend line: a 100x100 box stretched over the bar area (above the month labels);
                    each point sits over the middle of its month */}
                <svg
                  className="pointer-events-none absolute inset-x-0 top-0 z-10 h-[calc(100%-3rem)] w-full"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  aria-hidden
                >
                  <polyline
                    points={trendLine.map((value, index) => {
                      const x = ((index + 0.5) / trendLine.length) * 100;
                      const y = 100 - Math.min(100, Math.max(0, maxAmount > 0 ? (value / maxAmount) * 100 : 0));
                      return `${x},${y}`;
                    }).join(' ')}
                    fill="none"
                    stroke="#22c55e"
                    strokeWidth="2"
                    strokeDasharray="4,4"
                    vectorEffect="non-scaling-stroke"
                    opacity="0.8"
                  />
                </svg>

                <div className="flex items-end justify-between gap-1 h-full pb-12">
                  {monthlyData.map((amount, index) => {
                    const currentHeightPercent = maxAmount > 0 ? (amount / maxAmount) * 100 : 0;
                    const lastYearHeightPercent = maxAmount > 0 ? (lastYearData[index] / maxAmount) * 100 : 0;
                    return (
                      <div key={index} className="flex-1 flex flex-row items-end h-full justify-center gap-0.5 group relative">
                        {/* Last Year Bar */}
                        <div
                          className="bg-gray-300 w-5/12 rounded-t-sm transition-all duration-500 hover:bg-gray-400 cursor-pointer"
                          style={{ height: `${lastYearHeightPercent}%` }}
                          title={`${months[index]}: ${lastYearData[index].toLocaleString('sv-SE', {
                            style: 'currency',
                            currency: 'SEK',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}`}
                        />
                        {/* Current Year Bar */}
                        <div
                          className="bg-gradient-to-t from-blue-600 to-blue-400 w-5/12 rounded-t-sm transition-all duration-500 hover:from-blue-700 hover:to-blue-500 cursor-pointer"
                          style={{ height: `${currentHeightPercent}%` }}
                          title={`${months[index]}: ${amount.toLocaleString('sv-SE', {
                            style: 'currency',
                            currency: 'SEK',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}`}
                        />
                        {/* Tooltip */}
                        <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded py-2 px-3 whitespace-nowrap z-10">
                          <div className="font-semibold mb-1">{months[index]}</div>
                          <div className="text-blue-300">
                            Detta år: {amount.toLocaleString('sv-SE', {
                              style: 'currency',
                              currency: 'SEK',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </div>
                          <div className="text-gray-300">
                            Förra året: {lastYearData[index].toLocaleString('sv-SE', {
                              style: 'currency',
                              currency: 'SEK',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </div>
                          {lastYearData[index] > 0 && (
                            <div className={`mt-1 font-medium ${amount >= lastYearData[index] ? 'text-green-400' : 'text-red-400'}`}>
                              {((amount - lastYearData[index]) / lastYearData[index] * 100).toFixed(1)}%
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* X-axis labels */}
                <div className="absolute bottom-0 left-0 right-0 flex justify-between">
                  {months.map((month, index) => (
                    <div key={index} className="flex-1 text-center text-xs text-gray-500">
                      {month}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          </>
          )}
        </main>
      </div>
    </div>
  );
}
